import React, { useState } from 'react';
import { cn } from '@/lib/utils';

interface GalleryImage {
  id: number;
  before: string;
  after: string;
  category: string;
  title: string;
}

const galleryImages: GalleryImage[] = [
  {
    id: 1,
    before: "/images/gallery/bridal-before.jpg",
    after: "/images/gallery/bridal-after.jpg",
    category: "Makeup",
    title: "Bridal Transformation"
  },
  {
    id: 2,
    before: "/images/gallery/hair-before.jpg",
    after: "/images/gallery/hair-after.jpg",
    category: "Hair",
    title: "Hair Treatment Makeover"
  },
  {
    id: 3,
    before: "/images/gallery/facial-before.jpg",
    after: "/images/gallery/facial-after.jpg",
    category: "Facial",
    title: "Skin Rejuvenation"
  },
  {
    id: 4,
    before: "/images/gallery/party-before.jpg",
    after: "/images/gallery/party-after.jpg",
    category: "Makeup",
    title: "Party Makeup"
  }
];

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  
  return (
    <section id="gallery" className="section-padding bg-white">
      <div className="container-custom">
        <div className="text-center mb-12 reveal">
          <h2 className="text-3xl md:text-4xl font-bold text-beauty-rose mb-4">Transformation Gallery</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            See the results of our expertise through these before and after transformations.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {galleryImages.map((image, index) => (
            <div 
              key={image.id} 
              className={cn(
                "bg-white rounded-lg overflow-hidden shadow-lg cursor-pointer transition-transform hover:scale-[1.02] reveal",
                index % 2 === 0 ? "reveal" : "reveal-right"
              )}
              onClick={() => setSelectedImage(image)}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="grid grid-cols-2 gap-2">
                <div className="relative">
                  <img 
                    src={image.before} 
                    alt={`Before - ${image.title}`} 
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute bottom-2 left-2 bg-black/70 text-white text-xs px-2 py-1 rounded">Before</div>
                </div>
                <div className="relative">
                  <img 
                    src={image.after} 
                    alt={`After - ${image.title}`} 
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute bottom-2 left-2 bg-beauty-rose text-white text-xs px-2 py-1 rounded">After</div>
                </div>
              </div>
              <div className="p-4">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-playfair font-medium">{image.title}</h3>
                  <span className="text-sm bg-beauty-pink/30 text-beauty-rose rounded-full px-3 py-1">
                    {image.category}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {selectedImage && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
              <div className="p-4 flex justify-between items-center border-b">
                <h3 className="text-xl font-playfair font-bold">{selectedImage.title}</h3>
                <button 
                  onClick={() => setSelectedImage(null)} 
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <img 
                      src={selectedImage.before} 
                      alt={`Before - ${selectedImage.title}`} 
                      className="w-full h-auto rounded"
                    />
                    <p className="text-center font-medium">Before</p>
                  </div>
                  <div className="space-y-2">
                    <img 
                      src={selectedImage.after} 
                      alt={`After - ${selectedImage.title}`} 
                      className="w-full h-auto rounded"
                    />
                    <p className="text-center font-medium text-beauty-rose">After</p>
                  </div>
                </div>
                <div className="mt-6">
                  <p className="text-gray-600">
                    This transformation showcases our professional {selectedImage.category.toLowerCase()} services. 
                    We take pride in enhancing your natural beauty and providing exceptional results.
                  </p>
                </div>
              </div>
              <div className="border-t p-4 flex justify-end">
                <a 
                  href="#contact" 
                  onClick={() => setSelectedImage(null)}
                  className="btn-primary"
                >
                  Book This Service
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default Gallery;
